/* eslint-disable no-console */
import _get from 'lodash/get';
import { VIDEO_EVENTS, VIDEO_EVENTS_THROTTLE } from '@bedrock/media-engine';
import { httpRequestTimeout, request } from '../../../../utils/src/http';
import { parseAvailabilityStartTime } from '../../utils/dash.utils';
import { toSeconds } from '../../../../utils/src/time';
import { AUDIO_TRACK_CHANGED, SUBTITLES_TRACK_CHANGED, TRACKS_CHANGED } from '../../constants/events';
import { fetchUpfrontToken } from '../../utils';
import Engine from '../engine';
import { drmErrorStates, playerErrorStates, playerStates } from './oipf.constants';
import { makeSourceURL, performTracksList } from './oipf.utils';
import * as INTERNAL_ERRORS from '../../constants/errors/internal';

const { TIME_UPDATE_THROTTLE } = VIDEO_EVENTS_THROTTLE;

export class OipfEngine extends Engine {
  constructor() {
    super();
    this.name = 'OipfEngine';
  }

  init(element) {
    this._player = element;

    try {
      if (this.options.size) {
        this._player.removeAttribute('_opera_dae_full_screen');
        this._player.setFullScreen(false);
        this._player.style.width = this.options.size.width;
        this._player.style.height = this.options.size.height;
        console.log(`[OIPF] Player size: ${this.options.size.width} x ${this.options.size.height}`);
      } else {
        console.log('[OIPF] Player set to full-screen');
        this._player.setFullScreen(true);
      }
      this._nativeConfigObject = window.oipfObjectFactory.createConfigurationObject();
    } catch (error) {
      this.dispatchInternalError(INTERNAL_ERRORS.INTERNAL_ERROR_GET_PLAYER, 'OIPF implementation unsupported.', error);
      this.reset();

      return;
    }

    this._seekFrom = -1;
    this._seekTo = 0;

    this.setEventListeners();
  }

  async start(source) {
    if (!this._player) {
      console.error('[OIPF]: Player is not created or already destroyed.');

      return;
    }

    this.notifyPlayerInfo();

    this._startTime = source.startTime || 0;
    this._availabilityStartTimeInSeconds = 0;
    this._isLive = Boolean(source.isLive);
    let token = null;

    try {
      [token] = await Promise.all([
        fetchUpfrontToken(_get(this.options, 'drm.token.handler')),
        this._extractManifestData(source.reference),
      ]);
    } catch (error) {
      this.dispatchInternalError(
        INTERNAL_ERRORS.INTERNAL_ERROR_NO_SOURCE,
        'OIPFEngine failed to fetch DRM upfront token.',
        error,
      );

      this.reset();

      return;
    }

    // NOTE: the engine could have been released while the token and the manifest were requested
    if (!this._player) {
      return;
    }

    this._player.data = makeSourceURL(source.reference, token, _get(this.options, 'drm.settings'));
    this.options.dispatcher(VIDEO_EVENTS.LOAD_START);
    this.play();
    if (this._isLive) {
      this.seek(Math.round(Date.now() / 1000));
    }
  }

  setEventListeners() {
    this._player.onPlayStateChange = () => this._onPlayStateChange();
    this._player.onDRMRightsError = () => this._onDRMRightsError();
    this._player.onReadyToPlay = () => this._onReadyToPlay();
    this._player.onPlayPositionChanged = () => this._onPlayPositionChanged();
  }

  resetEventListeners() {
    if (this._player) {
      this._player.onPlayStateChange = null;
      this._player.onDRMRightsError = null;
      this._player.onReadyToPlay = null;
      this._player.onPlayPositionChanged = null;
    }
  }

  async _extractManifestData(reference) {
    /** @type {XMLDocument} */
    let manifest;

    try {
      manifest = await request(reference, { responseType: 'document', timeout: httpRequestTimeout });
    } catch (error) {
      console.error('[OIPF] Failed to fetch current DASH manifest');

      return;
    }

    if (this._isLive) {
      try {
        const availabilityStartTime = parseAvailabilityStartTime(manifest);
        this._availabilityStartTimeInSeconds = toSeconds(new Date(availabilityStartTime).getTime());
      } catch (error) {
        console.error('[OIPF] Failed to parse availability start time');
      }
    }

    try {
      const adaptationSets = manifest.querySelectorAll('AdaptationSet[contentType="text"]');

      this._subtitlesRoles = Array.from(adaptationSets).map(track =>
        Array.from(track.querySelectorAll('Role')).map(node => node.getAttribute('value')),
      );
    } catch (error) {
      console.error(`[OIPF] Failed to read subtitles from manifest: ${error}`);
    }

    try {
      const representations = Array.from(
        this._isLive
          ? manifest.querySelectorAll('AdaptationSet[mimeType="video/mp4"] Representation')
          : manifest.querySelectorAll('AdaptationSet[contentType="video"] Representation'),
      ).map(track => ({
        id: track.getAttribute('id'),
        bandwidth: parseInt(track.getAttribute('bandwidth'), 10),
        width: parseInt(track.getAttribute('width'), 10),
        height: parseInt(track.getAttribute('height'), 10),
      }));

      this._player.representations = representations;
    } catch (error) {
      console.error(`[OIPF] Failed to read video tracks from manifest: ${error}`);
    }
  }

  setAudioTrackById(trackId) {
    this._activeAudioTrack = this._changeTrack(this._audioTracks, trackId, this._activeAudioTrack, AUDIO_TRACK_CHANGED);
  }

  setSubtitlesTrackById(trackId) {
    if (this._activeSubtitlesTrack) {
      this._activeSubtitlesTrack.active = false;
      this._player.unselectComponent(this._activeSubtitlesTrack.raw);
    }

    const track = this._subtitlesTracks ? this._subtitlesTracks.find(track => track.id === trackId) : null;

    if (track) {
      console.log(`[OIPF] Setting subtitles track by ID: ${trackId}, ${track.language}`);
      this._activeSubtitlesTrack = this._changeTrack(
        this._subtitlesTracks,
        trackId,
        this._activeSubtitlesTrack,
        SUBTITLES_TRACK_CHANGED,
      );
    }
  }

  backToLive() {
    if (!this._player) {
      return;
    }

    const { end } = this.getSeekRange();
    if (Number.isFinite(end)) {
      this.seek(end / 1000 + this._availabilityStartTimeInSeconds);
    }
  }

  /**
   * Points player to a specific timestamp
   *
   * @param {number} position absolute position to seek, in seconds
   */
  seek(position) {
    if (!this._player) {
      return false;
    }
    this._seekFrom = this._getCurrentProgress() + this._availabilityStartTimeInSeconds;
    this._seekTo = position - this._availabilityStartTimeInSeconds - this._getStartDate() / 1000;

    const playTime = toSeconds(_get(this._player, 'playTime', 0));
    if (this._seekTo && playTime && this._seekTo >= playTime) {
      this._seekTo -= 2;
    }

    if (this._seekTo !== this._seekFrom) {
      this.options.dispatcher(VIDEO_EVENTS.SEEKING, {
        from: this._seekFrom,
        to: this._seekTo,
      });

      return this._player.seek(this._seekTo * 1000);
    }

    return false;
  }

  play() {
    try {
      this._player.play(1);
      if (this._playbackStarted) {
        return;
      }

      this._onTimeUpdateInterval = setInterval(() => this._onProgressUpdate(), TIME_UPDATE_THROTTLE);
      this._player.seek(this._isLive ? Date.now() : this._startTime * 1000);
      this._playbackStarted = true;
    } catch (error) {
      console.error('[OIPF]: Player is not created or already destroyed.');
    }
  }

  pause() {
    try {
      this._player.play(0);
    } catch (error) {
      console.error('[OIPF]: Player is not created or already destroyed.');
    }
  }

  reset() {
    clearInterval(this._onTimeUpdateInterval);
    this.resetEventListeners();

    this._onTimeUpdateInterval = null;
    this.options = null;
    if (this._player) {
      try {
        this._player.stop();
      } catch (error) {
        console.error('[OIPF]: OIPF implementation in not supported.');
      }
    }
    this._player = null;
    this._subtitlesRoles = null;
    this._playbackInitialized = false;
    this._playbackStarted = false;
    this._tracksDetected = false;
    this._activeAudioTrack = null;
    this._activeSubtitlesTrack = null;
    this._audioTracks = null;
    this._subtitlesTracks = null;
    this._nativeConfigObject = null;
    this._previousPosition = 0;
    this._seekFrom = -1;
  }

  get player() {
    return this._player;
  }

  /**
   * Get available seek range
   *
   * @returns values in milliseconds
   */
  getSeekRange() {
    let start = 0;
    let end = 0;
    const playPosition = this._getPlayPosition() + this._getStartDate();
    if (this._player.getAvailablePlayTime) {
      end = playPosition + this._player.getAvailablePlayTime(true);
      start = end >= this._player.getAvailablePlayTime(false) ? end - this._player.getAvailablePlayTime(false) : 0;
    } else {
      // this range is applicable for on-demand streams
      const duration = this._player.playTime;
      if (Number.isFinite(duration) && playPosition < duration) {
        start = 0;
        end = duration;
      } else {
        console.error('[OIPF]: Seek range is not available. Can not obtain playTime.');
      }
    }

    return {
      start: Number.isFinite(start) ? start : 0,
      end: Number.isFinite(end) ? end : 0,
    };
  }

  /**
   * Get current play position
   *
   * @returns value in milliseconds
   */
  _getPlayPosition() {
    return _get(this._player, 'playPosition', 0);
  }

  /**
   * Get current progress
   *
   * @returns value in seconds
   */
  _getCurrentProgress() {
    // NOTE: the payer does not include the time interval in between the AvailabilityStartTime and the first
    // Period start time for Live streams and calculates the playPosition (currentTime analogue) value only
    // beginning from the timeShiftBufferDepth. As a solution we simply add this missed timeline
    // which is called as "startDate", a bit confusing name thought.
    // For VOD the startDate value equals to 0.

    return (this._getPlayPosition() + this._getStartDate()) / 1000;
  }

  /**
   * Get the time gap starting from the AvailabilityStartTime and till the first Period start time for Live streams.
   * When the VOD stream is loaded returns 0
   *
   * @returns value in milliseconds
   */
  _getStartDate() {
    return this._isLive ? _get(this._player, 'startDate', 0) : 0;
  }

  _onPlayStateChange() {
    switch (this._player.playState) {
      case playerStates.BUFFERING:
        this._onBuffering();
        break;
      case playerStates.PLAYING:
        this._onPlay();
        break;
      case playerStates.PAUSED:
        this._onPause();
        break;
      case playerStates.FINISHED:
        this._onPause();
        this._onEnded();
        break;
      case playerStates.ERROR:
        this.dispatchInternalError(INTERNAL_ERRORS.PLAYER_ERROR, playerErrorStates[this._player.error].message, {
          code: playerErrorStates[this._player.error].code,
        });

        this.reset();
        break;
      case playerStates.CONNECTING:
      case playerStates.STOPPED:
      default:
        console.log(`[OIPF] Playback state: ${this._player.playState}`);
        break;
    }
  }

  _onDRMRightsError(errorState) {
    this.dispatchInternalError(INTERNAL_ERRORS.PLAYER_ERROR, drmErrorStates[errorState].message, {
      code: drmErrorStates[errorState].code,
    });

    this.reset();
  }

  _onReadyToPlay() {
    console.log('[OIPF] Ready to play');
    const activeAudio = this._player.getCurrentActiveComponents(this._player.COMPONENT_TYPE_AUDIO);
    for (let i = 0; i < activeAudio.length; i += 1) {
      if (this.options.mute) {
        console.log('[OIPF] Muting audio:', activeAudio);
        this._player.unselectComponent(activeAudio[i]);
      } else {
        console.log('[OIPF] Active audio:', activeAudio);
      }
    }

    if (!this._playbackInitialized) {
      this.options.dispatcher(VIDEO_EVENTS.CAN_PLAY);
      this._playbackInitialized = true;
    }
  }

  _onPlayPositionChanged() {
    if (this._seekFrom >= 0) {
      this.options.dispatcher(VIDEO_EVENTS.SEEKED, {
        from: this._seekFrom,
        to: Math.round(this._getCurrentProgress()),
      });
      this._seekFrom = -1;
    }
  }

  _onPlay() {
    if (!this._tracksDetected) {
      // As playing state can be fired by native player before buffering complete, need to check tracks several times
      try {
        this._audioTracks = performTracksList(this._player, this._player.COMPONENT_TYPE_AUDIO);
        this._subtitlesTracks = performTracksList(this._player, this._player.COMPONENT_TYPE_SUBTITLE).map(
          (track, index) => {
            if (this._subtitlesRoles) {
              if (this._subtitlesRoles[index]) {
                const roles = this._subtitlesRoles[index].join(', ');
                console.log(`[OIPF] Track ${track.id}, ${track.language} roles: ${roles} at index ${index}`);
              } else {
                console.log(
                  `[OIPF] There are no matching roles for track ${track.id}, ${track.language} at index ${index}`,
                );
              }
            } else {
              console.log('[OIPF] There are no roles for subtitles at all');
            }

            return {
              ...track,
              roles: (this._subtitlesRoles && this._subtitlesRoles[index]) || track.roles,
            };
          },
        );

        try {
          const activeSubtitles = this._player.getCurrentActiveComponents(this._player.COMPONENT_TYPE_SUBTITLE);
          for (let i = 0; i < activeSubtitles.length; i += 1) {
            console.log(`[OIPF] Unselecting default subtitles track ${activeSubtitles[i].pid}`);
            this._player.unselectComponent(activeSubtitles[i]);
          }
        } catch (error) {
          console.error(`[OIPF] Can not unselect default subtitles: ${error ? error.message : 'unknown error'}`);
        }

        if (this._audioTracks || this._subtitlesTracks) {
          this._audioTracks = this._audioTracks || [];
          this._subtitlesTracks = this._subtitlesTracks || [];

          this._tracksDetected = this._audioTracks.length || this._subtitlesTracks.length;
          this.options.dispatcher(TRACKS_CHANGED, {
            audioTracks: this._audioTracks,
            subtitleTracks: this._subtitlesTracks,
          });
        }
      } catch (error) {
        console.error(`[OIPF]: _onPlay : ${error.toString()}`);
      }
    }

    if (this._playbackStarted) {
      this._onBufferingEnded();
      this.options.dispatcher(VIDEO_EVENTS.PLAY);
      this.options.dispatcher(VIDEO_EVENTS.PLAYING);
    }
  }

  _onPause() {
    this._onBufferingEnded();
    this.options.dispatcher(VIDEO_EVENTS.PAUSE);
  }

  _onBuffering() {
    if (this._isBuffering) return;
    this._isBuffering = true;
    this.options.dispatcher(VIDEO_EVENTS.WAITING, { isBuffering: true });
  }

  _onBufferingEnded() {
    if (!this._isBuffering) return;
    this._isBuffering = false;
    this.options.dispatcher(VIDEO_EVENTS.WAITING, { isBuffering: false });
  }

  _onEnded() {
    this.options.dispatcher(VIDEO_EVENTS.ENDED);
  }

  _onProgressUpdate() {
    if (this.player.playState !== playerStates.PLAYING) return;
    let currentTime = this._getCurrentProgress();
    let liveEdge = this._isLive ? this.getSeekRange().end / 1000 : 0;

    if (currentTime !== this._previousPosition) {
      if (
        this._availabilityStartTimeInSeconds &&
        this._getStartDate() &&
        this._availabilityStartTimeInSeconds * 1000 > this._getStartDate()
      ) {
        currentTime += this._availabilityStartTimeInSeconds;
        liveEdge += this._availabilityStartTimeInSeconds;
      }
      this.options.dispatcher(VIDEO_EVENTS.TIME_UPDATE, {
        currentTime,
        liveEdge,
      });
      this._previousPosition = currentTime;
    }
  }

  _changeTrack(tracks, trackId, previousTrack, eventId) {
    const currentTrack = (tracks || []).find(track => track.id === trackId);

    if (currentTrack) {
      currentTrack.active = true;
      if (previousTrack) {
        previousTrack.active = false;
      }
      // TODO define is there some API to handle tracks change event (I haven't found any)
      this.options.dispatcher(eventId, {
        previousTrack,
        currentTrack,
      });

      console.log(`[OIPF] Dispatching ${eventId} with a track ${currentTrack.id}, ${currentTrack.language}`);

      this._player.selectComponent(currentTrack.raw);

      return currentTrack;
    }

    return previousTrack;
  }
}

OipfEngine.PLAYER_NAME = 'Bedrock-OIPF';

OipfEngine.PLAYER_VERSION = '1.0.0';
